/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Link } from "react-router-dom";
import { Label, Dropdown, Loader, Table, Spacer } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { Fieldset } from "~/components/Forms";
import { ServiceShortInitialState } from "~/components/Services/initialState";
import { ServiceAttachment } from "~/components/Services/types";
import { ServiceCmtSmall, Sla } from "~/models";
import { ARAttachedServices } from "~/models/assignmentRule";
import { ServiceGroup } from "~/models/serviceGroup";
import { ROUTES } from "~/routing";
import { formatServiceName, useAxios, useTimeoutHover } from "~/tools";
import { PreviewContent } from "./PreviewContent";

type ServiceGroupSelectProps = {
  serviceAttachments?: ServiceAttachment[] | ARAttachedServices[];
  setSelectedId: React.Dispatch<React.SetStateAction<string>>;
  selectedId: string;
};

const selectedServiceInitialState = {
  name: "",
  value: "",
};

function ServiceGroupSelect({
  serviceAttachments,
  setSelectedId,
  selectedId,
}: ServiceGroupSelectProps) {
  const [isLoading, setIsLoading] = React.useState(true);

  const [serviceGroups, setServiceGroups] = React.useState([
    ServiceShortInitialState,
  ]);
  const [page, setPage] = React.useState(0);
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const serviceGroupsListIds = serviceAttachments?.map(
    (i: ServiceAttachment | ARAttachedServices) => i.service?.serviceGroup?.id
  );
  const filterServiceGroup = serviceGroups
    ?.filter((item: ServiceGroup) => !serviceGroupsListIds?.includes(item.id))
    .filter((i: ServiceGroup) => i.id.length !== 0);

  const servicesWithinGroup =
    serviceGroups.find((item: ServiceGroup) => item.id === selectedId) ||
    ServiceShortInitialState;
  const fetchServiceGroup = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(
        `/catalog/serviceGroups?limit=${50}&page=${page}`
      );
      data.totalPages > 1 && data.totalPages > page + 1 && setPage(page + 1);
      data.serviceGroups &&
        setServiceGroups((prevState: ServiceGroup[]) => [
          ...prevState,
          ...data.serviceGroups,
        ]);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, page, showError]);

  React.useEffect(() => {
    fetchServiceGroup();
  }, [fetchServiceGroup]);

  const { setTimeoutHoverAction, clearTimeoutHover } = useTimeoutHover();

  const [sla, setSla] = React.useState<Sla[]>([]);
  const [selectedService, setSelectedService] = React.useState(
    selectedServiceInitialState
  );

  const fetchSlas = React.useCallback(async () => {
    const serviceId = selectedService.value;
    try {
      const { data } = await Axios().get(`/catalog/services/${serviceId}/slas`);
      setSla(data.slas);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, selectedService.value, showError]);

  React.useEffect(() => {
    selectedService.value?.length !== 0 && fetchSlas();
  }, [selectedService.value?.length, fetchSlas]);

  return (
    <>
      <Fieldset>
        <Label name="Service Group" />
        {isLoading ? (
          <Loader inContent />
        ) : (
          <Dropdown
            onChange={(g: string) => {
              setSelectedId(g);
              setSelectedService(selectedServiceInitialState);
            }}
            value={selectedId}
            options={
              filterServiceGroup?.map((serviceItem) => ({
                value: serviceItem.id,
                label: formatServiceName(serviceItem),
              })) || []
            }
            name="service group"
          />
        )}
      </Fieldset>
      {selectedId && (
        <Table small>
          <Table.thead>
            <Table.tr>
              <Table.th css={{ width: "100%" }}>Service Name</Table.th>
            </Table.tr>
          </Table.thead>
          <Table.tbody>
            {servicesWithinGroup?.services?.map(
              (serviceItem: ServiceCmtSmall) => (
                <Table.tr key={serviceItem.id}>
                  <Table.td
                    css={{ width: "100%" }}
                    onMouseOver={() =>
                      setTimeoutHoverAction(
                        () =>
                          setSelectedService({
                            name: formatServiceName(serviceItem),
                            value: serviceItem.id,
                          }),
                        300
                      )
                    }
                    onMouseLeave={() => clearTimeoutHover()}
                  >
                    <Link
                      to={`${ROUTES.serviceCommitments.index}/${serviceItem.id}`}
                    >
                      {formatServiceName(serviceItem)}
                    </Link>
                  </Table.td>
                </Table.tr>
              )
            )}
          </Table.tbody>
        </Table>
      )}
      <Spacer height="1rem" />
      <PreviewContent
        sla={sla}
        selectedService={selectedService}
        isLoading={isLoading}
      />
    </>
  );
}

export { ServiceGroupSelect };
